import React from "react";
import { useTemplate } from "./api";
import { resetRenderLimits } from "./RenderLimiter";
import { reportContext } from "./ReportContext";
import { IReportContext, IReportSlide } from "./ReprortContextTypes";

const ReportContextProvider = reportContext.Provider;

const Report = (
  props: React.PropsWithChildren<{
    isReady?: boolean;
    template: string;
    progressMax?: number;
    onProgress?: (percentage: number) => void;
    onNothingToRender: () => void;
    onReadyToRender: (report: IReportContext) => void;
  }>
) => {
  // TODO: load the report template from the backend

  const {
    data: templateData,
    isLoading,
    isRefetching,
    isPreviousData,
  } = useTemplate(props.template);

  const progressMax = Math.max(90, props.progressMax || 0);
  const progressMin = 1;
  const reportProgress = React.useRef(progressMin);

  if (props.onProgress) {
    props.onProgress?.(reportProgress.current);
  }

  const reportRef = React.useRef<IReportContext>({
    templateName: props.template,
    slides: [],
    addSlide: (newSlide: IReportSlide) => {
      console.log("slide: add slide called ", newSlide);
      reportRef.current?.slides?.push(newSlide);
    },
    onSlideProgress: (slide) => {
      const report = reportRef.current;
      if (report) {
        let sum = 0;
        report.slides?.forEach((s) => {
          sum += s.slideProgress || 0;
        });
        if (report.slides?.length) {
          const progressPros = sum / report.slides?.length;
          reportProgress.current = (progressMax - progressMin) * progressPros;
          props.onProgress?.(reportProgress.current);
        }
      }
    },
    onSlideReady: (newSlide: IReportSlide) => {
      console.log("slide: onSlideReady called ", newSlide);
      newSlide.isReady = true;
      const report = reportRef.current;
      /*
      if (report) {
        const totalCnt = report.slides?.length || 0;
        const currentCnt = report.slides?.filter((s) => s.isReady).length || 0;

        if (totalCnt > 0) {
          reportProgress.current =
            (progressMax - progressMin) * (currentCnt / totalCnt);
          props.onProgress?.(reportProgress.current);
        }
      }
      */
      reportRef.current?.checkReadiness?.();
    },
    checkReadiness: () => {
      if (
        reportRef.current.hasRenderedAtLeastOnce &&
        !reportRef.current.isReady
      ) {
        // Check if all slides are actually done at this point
        const slideCount = reportRef.current.slides?.length || 0;
        const readySlidesCount =
          reportRef.current.slides?.filter((slide) => slide.isReady).length ||
          0;
        if (slideCount === 0) {
          reportRef.current.isReady = true;
          props.onNothingToRender();
        } else {
          if (slideCount === readySlidesCount) {
            reportRef.current.isReady = true;
            props.onReadyToRender(reportRef.current);
            return;
          }
        }
      }
    },
  });

  React.useEffect(() => {
    resetRenderLimits();
  }, []);

  React.useEffect(() => {
    if (isLoading || isPreviousData || isRefetching) {
      return;
    }
    if (reportRef && reportRef.current && templateData) {
      reportRef.current.templateSlides = templateData.pages;
      reportRef.current.hasRenderedAtLeastOnce = true;
      reportRef.current.checkReadiness?.();
    }
  }, [reportRef, isLoading, isPreviousData, isRefetching]);

  if (isLoading || isPreviousData || isRefetching) {
    return null;
  }

  if (reportRef && reportRef.current && templateData) {
    reportRef.current.templateSlides = templateData.pages;
  }

  return (
    <div style={{ position: "absolute", left: "-8000px", top: "0px" }}>
      <h1>Report engine testing</h1>
      <ReportContextProvider value={reportRef.current}>
        {props.children}
      </ReportContextProvider>
    </div>
  );
};

export default Report;
